import { useMemo } from 'react';
import { useGetWorkSummaryQuery } from '../../../../redux/api/fymAPI';
import { WorkSummary } from '../../../../redux/api/fymAPI.types';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';
import { useFailuresSeen } from '../../promote/work/assetsFailuresSeen';

/** The poll of the summary while something works and the socket is down. */
export const KIT_WORK_BADGE_POLL_MS = 30 * 1000;

export interface KitWork {
  /** The kit assets and AI reels of the artist that are queued or being made. */
  working: number;
  /** The open failures, when the newest one is newer than the last visit that saw the failures. */
  newFailed: number;
  /** Every open failure, seen or not: the Work chip on Studio counts them. */
  failed: number;
}

/**
 * The counts of the badge from the summary. Pure. A failure leaves the count on the server after
 * "Try again" or after 7 days (owner answer 2, 2026-10-06); a visit of the Work page, the Posts
 * page or a Kit tab that shows the failures hides the red badge until a newer failure comes.
 */
export function kitWorkOf(summary: WorkSummary, failuresSeenAt: number): KitWork {
  const failedAt = summary.lastFailedAt === null ? 0 : Date.parse(summary.lastFailedAt);
  return {
    working: summary.working,
    newFailed: summary.failed > 0 && failedAt > failuresSeenAt ? summary.failed : 0,
    failed: summary.failed,
  };
}

/**
 * The work counts of the active artist (design release-kit t3). The Studio menu item shows the new
 * failures as a red count, and the Studio page shows "2 working · 1 failed" as a chip (design
 * navigation-v2 c 3e). It reads
 * `GET /accounts/:id/work/summary?scope=assets` of the active artist: the kit assets and the AI
 * reels of BOTH kinds. A visit of the Work page, of the Posts page or of a Kit tab that shows the
 * failures clears the red badge. The realtime events of the kit assets and the reels invalidate
 * the `Work` tag, so it polls only while the socket is down and something works.
 */
export function useKitWorkBadge(accountId: number | null): KitWork | undefined {
  const { seenAt } = useFailuresSeen(accountId);
  const { data } = useGetWorkSummaryQuery({ accountId: accountId ?? 0 }, { skip: accountId === null });
  const work = useMemo(() => (data === undefined ? undefined : kitWorkOf(data, seenAt)), [data, seenAt]);
  const pollingInterval = useRealtimePollInterval(work !== undefined && work.working > 0 ? KIT_WORK_BADGE_POLL_MS : 0);
  // A second subscription to the same cache entry sets the poll; RTK Query takes the shortest interval.
  useGetWorkSummaryQuery(
    { accountId: accountId ?? 0 },
    { skip: accountId === null || pollingInterval === 0, pollingInterval }
  );
  return work;
}
